import type { AnyAction, Middleware, Store } from 'redux';
import { matchRoutes, type Location, type createBrowserRouter } from 'react-router-dom';
import { pipe, Option, Array as A } from 'effect';

import { LOCATION_CHANGE } from '@/domain/router/redux/reduxActions';
import { setRouteCompanyIdAction } from '@/domain/router/RouterActions';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';

type Routes = Parameters<typeof matchRoutes>[0];

export const companyIdOf = (routes: Routes, location: Location | string): string | null =>
  pipe(
    Option.fromNullable(matchRoutes(routes, location)),
    Option.flatMap(A.last),
    Option.flatMap((match) => Option.fromNullable(match.params.companyId)),
    Option.getOrNull,
  );

type RouterLike = Pick<ReturnType<typeof createBrowserRouter>, 'routes' | 'subscribe'>;

// The company in scope follows the location the router committed. Subscribed at store creation,
// not in the navigator: navigations before the navigator starts (the post-login redirect) must
// reach the store too. A blocked navigation commits no location, so the page that blocked it
// keeps its company and its prompt.
export const subscribeRouteCompany = (router: RouterLike, store: Pick<Store, 'dispatch' | 'getState'>) =>
  router.subscribe(({ location }) => {
    const companyId = companyIdOf(router.routes, location);
    if (companyId !== currentCompanyIdSelector(store.getState())) store.dispatch(setRouteCompanyIdAction(companyId));
  });

const createRouteCompanyMiddleware =
  (routes: Routes): Middleware =>
  () =>
  (next) =>
  (action: AnyAction) =>
    action.type === LOCATION_CHANGE
      ? next({ ...action, payload: { ...action.payload, companyId: companyIdOf(routes, action.payload.location) } })
      : next(action);

export default createRouteCompanyMiddleware;
